import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ALL_MENUS, WINDOW_MENU, GAMEOBJECT_MENU, FILE_MENU, EDIT_MENU } from '../../constants/menus';
import { MenuDefinition, MenuItem } from '../../../core/types/editor.types';
import { usePanelStore } from '../../store/panelStore';
import { useSceneStore } from '../../store/sceneStore';
import { useBuildStore } from '../../store/buildStore';
import { useBackendGameStore } from '../../store/backendGameStore';
import type { BackendGameListItem, BackendGameConfigResponse } from '../../../core/types/backend-game.types';
import { useProjectStore } from '../../store/projectStore';
import { PanelId } from '../../../core/types/panel.types';
import { GameObjectType } from '../../../core/types/gameobject.types';
import { cn } from '../../utils/cn';
import { ProjectStatusIndicator } from './ProjectStatusIndicator';
import { UnsavedChangesDialog } from '../dialogs/UnsavedChangesDialog';
import { useProjectLifecycle } from '../../hooks/useProjectLifecycle';
import { useAppStore } from '../../store';
import { useProjectOpen } from '../../hooks/useProjectOpen';
import { scheduleConfigWrite } from '@renderer/services/slot-core/ConfigBridgeService';

interface MenuProps {
  menu: MenuDefinition;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onMouseEnter: () => void;
}

const MenuDropdown: React.FC<{
  items: MenuItem[];
  onClose: () => void;
  level?: number;
}> = ({ items, onClose, level = 0 }) => {
  const [hoveredSubmenu, setHoveredSubmenu] = useState<string | null>(null);

  return (
    <div
      className={cn(
        'absolute bg-zinc-800 border border-zinc-700 rounded-md shadow-xl py-1 min-w-[200px] z-[1000]',
        level === 0 ? 'top-full left-0 mt-1' : 'left-full top-0 -mt-1 ml-0'
      )}
    >
      {items.map((item) => {
        if (item.separator) {
          return <div key={item.id} className="h-px bg-zinc-700 my-1" />;
        }

        const hasSubmenu = item.submenu && item.submenu.length > 0;

        return (
          <div
            key={item.id}
            className="relative"
            onMouseEnter={() => hasSubmenu && setHoveredSubmenu(item.id)}
            onMouseLeave={() => setHoveredSubmenu(null)}
          >
            <button
              className={cn(
                'w-full px-3 py-1.5 text-left text-sm flex items-center justify-between',
                'hover:bg-zinc-700 transition-colors',
                item.disabled && 'opacity-50 cursor-not-allowed'
              )}
              disabled={item.disabled}
              onClick={() => {
                if (!hasSubmenu && item.action) {
                  item.action();
                  onClose();
                }
              }}
            >
              <span className="flex items-center gap-2">
                {item.checked !== undefined && (
                  <span className="w-4 text-center">
                    {item.checked ? '✓' : ''}
                  </span>
                )}
                <span>{item.label}</span>
              </span>
              <span className="flex items-center gap-2 text-zinc-500">
                {item.shortcut && <span className="text-xs">{item.shortcut}</span>}
                {hasSubmenu && <span>▶</span>}
              </span>
            </button>

            {hasSubmenu && hoveredSubmenu === item.id && (
              <MenuDropdown
                items={item.submenu!}
                onClose={onClose}
                level={level + 1}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const Menu: React.FC<MenuProps> = ({
  menu,
  isOpen,
  onOpen,
  onClose,
  onMouseEnter,
}) => {
  return (
    <div className="relative">
      <button
        className={cn(
          'px-3 py-1 text-sm hover:bg-zinc-700 rounded transition-colors',
          isOpen && 'bg-zinc-700'
        )}
        onClick={() => (isOpen ? onClose() : onOpen())}
        onMouseEnter={onMouseEnter}
      >
        {menu.label}
      </button>

      {isOpen && (
        <MenuDropdown items={menu.items} onClose={onClose} />
      )}
    </div>
  );
};

export const MenuBar: React.FC = () => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const { panels, togglePanel } = usePanelStore();
  const { createGameObject, selectedIds } = useSceneStore();
  const { openDialog: openBuildDialog } = useBuildStore();
  const { currentProject, isDirty, setDirty, lastSavedAt } = useProjectStore();
  const { games, loadGamesList, loadGameConfig } = useBackendGameStore();
  const { saveProject, closeProject, saveAndClose, isSaving, saveError } = useProjectLifecycle();
  const { openNewProjectDialog } = useAppStore();
  const { openProject: openProjectPicker } = useProjectOpen();
  const menuBarRef = useRef<HTMLDivElement>(null);

  // Load games list on mount (for Games submenu)
  useEffect(() => {
    loadGamesList().catch(() => {
      // Silently fail - games list is optional
    });
  }, [loadGamesList]);

  // Helper to create a backend game object with config data
  const createBackendGameObject = useCallback(async (game: BackendGameListItem) => {
    try {
      const configResponse: BackendGameConfigResponse = await loadGameConfig(game.gameName);
      const parentId = selectedIds.length > 0 ? selectedIds[0] : null;
      const newId = createGameObject('backend-slot-game', parentId);

      // Find the created object and its backend-game-config component
      const obj = useSceneStore.getState().gameObjects[newId];
      if (obj) {
        // Rename root game object to the actual game name
        const displayName = configResponse.name || game.gameName;
        if (displayName) {
          useSceneStore.getState().updateGameObject(newId, { name: displayName });
        }

        const configComp = obj.components.find(c => c.type === 'backend-game-config');
        if (configComp) {
          useSceneStore.getState().updateComponent(newId, configComp.id, {
            gameName: game.gameName,
            gameId: configResponse.gameId,
            name: configResponse.name,
            version: configResponse.version,
            description: configResponse.description,
            backendConfig: configResponse.config,
            activeMode: 0,
            symbolTextures: {},
          });

          // Trigger initial config write to slot-core/game.config.json
          scheduleConfigWrite();
        }
      }
    } catch (err) {
      console.error('[MenuBar] Failed to create backend game object:', err);
    }
  }, [loadGameConfig, selectedIds, createGameObject]);

  // Guard that shows unsaved dialog before performing an action when project is dirty
  const guardUnsavedChanges = useCallback((action: () => void) => {
    if (isDirty && currentProject) {
      setPendingAction(() => action);
      setShowUnsavedDialog(true);
    } else {
      action();
    }
  }, [isDirty, currentProject]);

  // Close project handler - shows dialog if dirty, otherwise closes directly
  const handleCloseProject = useCallback(() => {
    if (isDirty) {
      setShowUnsavedDialog(true);
    } else {
      closeProject();
    }
  }, [isDirty, closeProject]);

  // Dialog handlers
  const handleDontSave = useCallback(() => {
    setDirty(false);
    setShowUnsavedDialog(false);
    closeProject();
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [setDirty, closeProject, pendingAction]);

  const handleCancelClose = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingAction(null);
  }, []);

  const handleSaveAndClose = useCallback(async () => {
    const success = await saveAndClose();
    if (success) {
      setShowUnsavedDialog(false);
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    }
  }, [saveAndClose, pendingAction]);

  // Listen for unsaved close attempt from keyboard shortcuts
  useEffect(() => {
    const handleUnsavedCloseAttempt = () => {
      setShowUnsavedDialog(true);
    };

    window.addEventListener('project:unsaved-close-attempt', handleUnsavedCloseAttempt);
    return () => {
      window.removeEventListener('project:unsaved-close-attempt', handleUnsavedCloseAttempt);
    };
  }, []);

  // Build menus with connected actions
  const menusWithActions = useMemo(() => {
    return ALL_MENUS.map((menu) => {
      if (menu.id === 'window') {
        // Update Window menu with panel visibility and actions
        const updatedItems: MenuItem[] = WINDOW_MENU.items.map((item) => {
          // Panel toggle items
          const panelMap: Record<string, PanelId> = {
            'hierarchy': 'hierarchy',
            'scene-view': 'scene-view',
            'inspector': 'inspector',
            'project': 'project',
            'script-editor': 'script-editor',
            'animation-editor': 'animation-editor',
            'live-preview': 'live-preview',
            'console': 'console',
            'slot-asset-generator': 'slot-asset-generator',
            'slot-screen-generator': 'slot-screen-generator',
          };

          const panelId = panelMap[item.id];
          if (panelId) {
            return {
              ...item,
              checked: panels[panelId]?.visible ?? false,
              action: () => togglePanel(panelId),
            };
          }

          return item;
        });

        return { ...menu, items: updatedItems };
      }

      // Update GameObject menu with creation actions
      if (menu.id === 'gameobject') {
        const gameObjectTypeMap: Record<string, GameObjectType> = {
          'create-empty': 'empty',
          'create-sprite': 'sprite',
          'create-container': 'container',
          'create-text': 'text',
          'create-graphics': 'graphics',
        };

        const updatedItems: MenuItem[] = GAMEOBJECT_MENU.items.map((item) => {
          const gameObjectType = gameObjectTypeMap[item.id];
          if (gameObjectType) {
            return {
              ...item,
              action: () => {
                // Create as child of first selected object, or at root
                const parentId = selectedIds.length > 0 ? selectedIds[0] : null;
                createGameObject(gameObjectType, parentId);
              },
            };
          }

          // Populate dynamic Games submenu with backend games
          if (item.id === 'games-submenu') {
            const gamesSubmenuItems = games.map((game) => ({
              id: `backend-game-${game.gameName}`,
              label: game.name || game.gameName,
              action: () => createBackendGameObject(game),
            }));

            return {
              ...item,
              submenu: gamesSubmenuItems.length > 0
                ? gamesSubmenuItems
                : [{ id: 'no-games', label: 'No games available', disabled: true }],
            };
          }

          return item;
        });

        return { ...menu, items: updatedItems };
      }

      // Update File menu with build, close actions
      if (menu.id === 'file') {
        const updatedItems: MenuItem[] = FILE_MENU.items.map((item) => {
          if (item.id === 'close-project') {
            return {
              ...item,
              action: () => handleCloseProject(),
              disabled: !currentProject,
            };
          }

          if (item.id === 'build-settings') {
            return {
              ...item,
              action: () => openBuildDialog(),
            };
          }

          if (item.id === 'build') {
            return {
              ...item,
              action: () => {
                // Quick build - open dialog and start build
                openBuildDialog();
              },
            };
          }

          if (item.id === 'new-project') {
            return {
              ...item,
              action: () => guardUnsavedChanges(() => {
                openNewProjectDialog();
              }),
            };
          }

          if (item.id === 'open-project') {
            return {
              ...item,
              action: () => guardUnsavedChanges(() => {
                openProjectPicker();
              }),
            };
          }

          return item;
        });

        return { ...menu, items: updatedItems };
      }

      // Update Edit menu with actions
      if (menu.id === 'edit') {
        const updatedItems: MenuItem[] = EDIT_MENU.items.map((item) => {
          if (item.id === 'undo') {
            return {
              ...item,
              action: () => useSceneStore.temporal.getState().undo(),
            };
          }

          if (item.id === 'redo') {
            return {
              ...item,
              action: () => useSceneStore.temporal.getState().redo(),
            };
          }

          if (item.id === 'cut') {
            return {
              ...item,
              action: () => useSceneStore.getState().cutSelected(),
              disabled: selectedIds.length === 0,
            };
          }

          if (item.id === 'copy') {
            return {
              ...item,
              action: () => useSceneStore.getState().copySelected(),
              disabled: selectedIds.length === 0,
            };
          }

          if (item.id === 'paste') {
            return {
              ...item,
              action: () => useSceneStore.getState().paste(),
              disabled: useSceneStore.getState().clipboardIds.length === 0,
            };
          }

          if (item.id === 'duplicate') {
            return {
              ...item,
              action: () => {
                const state = useSceneStore.getState();
                state.selectedIds.forEach((id) => state.duplicateGameObject(id));
              },
              disabled: selectedIds.length === 0,
            };
          }

          if (item.id === 'delete') {
            return {
              ...item,
              action: () => useSceneStore.getState().deleteSelected(),
              disabled: selectedIds.length === 0,
            };
          }

          return item;
        });

        return { ...menu, items: updatedItems };
      }

      return menu;
    });
  }, [panels, togglePanel, createGameObject, selectedIds, openBuildDialog, currentProject, isDirty, handleCloseProject, games, createBackendGameObject, openNewProjectDialog, openProjectPicker, guardUnsavedChanges]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuBarRef.current && !menuBarRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenuId]);

  return (
    <>
      <div
        ref={menuBarRef}
        className="relative z-[1001] flex items-center justify-between h-8 bg-zinc-900 border-b border-zinc-800 px-2"
      >
        <div className="flex items-center gap-1">
          {menusWithActions.map((menu) => (
            <Menu
              key={menu.id}
              menu={menu}
              isOpen={openMenuId === menu.id}
              onOpen={() => setOpenMenuId(menu.id)}
              onClose={() => setOpenMenuId(null)}
              onMouseEnter={() => openMenuId && setOpenMenuId(menu.id)}
            />
          ))}
        </div>

        {currentProject && (
          <ProjectStatusIndicator
            projectName={currentProject.name || 'Untitled'}
            isDirty={isDirty}
            isSaving={isSaving}
            saveError={saveError}
            lastSavedAt={lastSavedAt}
            onSave={saveProject}
          />
        )}
      </div>

      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={handleCancelClose}
        projectName={currentProject?.name || 'Untitled'}
        onDontSave={handleDontSave}
        onCancel={handleCancelClose}
        onSave={handleSaveAndClose}
        isSaving={isSaving}
      />
    </>
  );
};
